/**
 * Tile - v5 — strict single-shape rule.
 *
 * Every tile renders the SAME structure with identical typography:
 *   1. Top row: icon bubble (34) + label (11px uppercase) + expand button (right)
 *   2. Value (30px, weight 700) — primary metric
 *   3. Bottom row: delta (11px) + subValue (11px)
 *
 * Tiles have a fixed minHeight so a row of tiles always lines up.
 * The only thing that varies between tiles is the pastel bg + accent color.
 * No size variants. No spotlight variants. One shape, one rule.
 */
import React, { useState } from "react";
import { tokensV2 as T } from "./tokens.v2";

const PASTELS = {
  "spotlight-orange":  { bg: "#FFEDD5", accent: "#FC8019" },
  "spotlight-yellow":  { bg: "#FFF3CC", accent: "#FFC107" },
  "spotlight-lime":    { bg: "#DCF1DD", accent: "#4CAF50" },
  "spotlight-magenta": { bg: "#FFE0EB", accent: "#FF6B9D" },
  "spotlight-red":     { bg: "#FFE0E4", accent: "#FF4757" },
  "spotlight-violet":  { bg: "#ECE3FF", accent: "#7C4DFF" },
  "spotlight-electric":{ bg: "#D1F2EE", accent: "#2EC4B6" },
  "muted-orange":      { bg: "#FFEDD5", accent: "#FC8019" },
  "muted-violet":      { bg: "#ECE3FF", accent: "#7C4DFF" },
  "muted-electric":    { bg: "#D1F2EE", accent: "#2EC4B6" },
  "muted-magenta":     { bg: "#FFE0EB", accent: "#FF6B9D" },
  "muted-red":         { bg: "#FFE0E4", accent: "#FF4757" },
  "muted-lime":        { bg: "#DCF1DD", accent: "#4CAF50" },
  "muted-yellow":      { bg: "#FFF3CC", accent: "#FFC107" },
};

const TILE_HEIGHT = 220;
const TILE_PADDING = "18px 20px";
const VALUE_FONT_SIZE = 34;
const LABEL_FONT_SIZE = 12;
const META_FONT_SIZE = 12;
const ICON_SIZE = 38;

// Smooth cubic-bezier sparkline with gradient fill, fading trailing edge,
// peak marker, and a live pulsing "now" point so it feels like more data is streaming in.
function Sparkline({ data, color, gradientId }) {
  if (!Array.isArray(data) || data.length < 2) return null;
  // Equal horizontal breathing room on both sides (12 each) so the curve sits
  // centred inside the card and the live-pulse aura on the right has matching
  // margin to the left start point.
  const w = 200, h = 40, padTop = 6, padBottom = 4, padLeft = 12, padRight = 12;
  const innerW = w - padLeft - padRight;
  const innerH = h - padTop - padBottom;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const span = Math.max(max - min, 1);
  const step = innerW / (data.length - 1);
  const points = data.map((v, i) => ({
    x: padLeft + i * step,
    y: padTop + innerH - ((v - min) / span) * innerH,
    v,
  }));
  let line = `M${points[0].x},${points[0].y}`;
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i];
    const p1 = points[i + 1];
    const cpx = p0.x + (p1.x - p0.x) / 2;
    line += ` C${cpx},${p0.y} ${cpx},${p1.y} ${p1.x},${p1.y}`;
  }
  const fill = `${line} L${points[points.length - 1].x},${h} L${points[0].x},${h} Z`;
  const last = points[points.length - 1];
  const uid = gradientId || `spark-${Math.round(Math.random() * 1e9)}`;
  const fillId = `${uid}-fill`;
  const fadeId = `${uid}-fade`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" width="100%" height={h} style={{ display: "block", overflow: "visible" }}>
      <defs>
        <linearGradient id={fillId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"  stopColor={color} stopOpacity="0.40" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
        {/* Right-edge horizontal fade — mask uses white = reveal, black = hide.
            Solid white from 0-80% so the bulk of the chart is visible, then
            fading to black past the last point so the trail dissolves cleanly. */}
        <linearGradient id={fadeId} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%"  stopColor="#FFFFFF" stopOpacity="1" />
          <stop offset="85%" stopColor="#FFFFFF" stopOpacity="1" />
          <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
        </linearGradient>
        <mask id={`${uid}-mask`}>
          <rect x="0" y="0" width={w} height={h} fill={`url(#${fadeId})`} />
        </mask>
      </defs>

      <path d={fill} fill={`url(#${fillId})`} mask={`url(#${uid}-mask)`} />
      <path d={line} fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
        vectorEffect="non-scaling-stroke" mask={`url(#${uid}-mask)`} />

      {/* Live "now" pulse — outer ring expands and fades to suggest streaming data */}
      <circle cx={last.x} cy={last.y} r="3.5" fill={color} fillOpacity="0.35">
        <animate attributeName="r" values="3.5;9;3.5" dur="1.8s" repeatCount="indefinite" />
        <animate attributeName="fillOpacity" values="0.35;0;0.35" dur="1.8s" repeatCount="indefinite" />
      </circle>
      <circle cx={last.x} cy={last.y} r="3" fill={color} />
    </svg>
  );
}

function BreakdownRow({ items, color }) {
  if (!Array.isArray(items) || items.length === 0) return null;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 12px", fontSize: 11, lineHeight: 1.3 }}>
      {items.slice(0, 4).map((it, i) => (
        <span key={i} style={{ display: "inline-flex", alignItems: "baseline", gap: 4, whiteSpace: "nowrap" }}>
          <span style={{ width: 5, height: 5, borderRadius: "50%", backgroundColor: color, display: "inline-block", alignSelf: "center" }} />
          <span style={{ color: T.text.muted, fontWeight: 600 }}>{it.label}</span>
          <span style={{ color: T.text.primary, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{it.value}</span>
        </span>
      ))}
    </div>
  );
}

function DeltaText({ direction = "flat", value, unit }) {
  const fg = direction === "up" ? T.status.good : direction === "down" ? T.status.bad : T.text.muted;
  const sign = direction === "up" ? "+" : direction === "down" ? "-" : "";
  // unit defaults to "vs prev" so the label stays accurate across every range
  // (today → "vs yesterday", week → "vs prev week", etc). Callers should pass
  // the API-supplied prev.label when available.
  const suffix = unit || "vs prev";
  return (
    <span style={{ fontSize: META_FONT_SIZE, fontWeight: 700, color: fg, fontVariantNumeric: "tabular-nums", fontFeatureSettings: T.font.nums, whiteSpace: "nowrap" }}>
      {sign}{value} {suffix}
    </span>
  );
}

function ExpandButton() {
  return (
    <span style={{ width: 26, height: 26, borderRadius: "50%", backgroundColor: "rgba(31,42,63,0.06)", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }} aria-hidden>
      <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
        <path d="M3 9 L9 3 M9 3 L4 3 M9 3 L9 8" stroke={T.text.secondary} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  );
}

function IconBubble({ icon, accent }) {
  const bg = accent ? accent + "33" : "rgba(31,42,63,0.08)";
  return (
    <span style={{ width: ICON_SIZE, height: ICON_SIZE, borderRadius: "50%", backgroundColor: bg, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0, color: accent || T.text.primary, fontSize: 17, fontWeight: 700 }}>{icon || "*"}</span>
  );
}

export function Tile({ label, value, subValue, delta, icon, onClick, variant = "default", sparkline, breakdown, title }) {
  const [hover, setHover] = useState(false);
  const interactive = typeof onClick === "function";
  const v = PASTELS[variant];
  const bg = v ? v.bg : T.surface.card;
  const accent = v ? v.accent : null;

  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      onMouseEnter={() => interactive && setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: bg, border: "none", borderRadius: 16, padding: TILE_PADDING,
        height: TILE_HEIGHT, minHeight: TILE_HEIGHT, boxSizing: "border-box",
        color: T.text.primary, cursor: interactive ? "pointer" : "default",
        // Lift + shadow only when interactive — otherwise the tile lies about
        // being clickable.
        transform: (hover && interactive) ? "translateY(-2px)" : "translateY(0)",
        transition: "transform 160ms ease, box-shadow 160ms ease",
        fontFamily: T.font.sans, outline: "none", width: "100%",
        boxShadow: (hover && interactive) ? "0 6px 18px rgba(31,42,63,0.10)" : "0 1px 2px rgba(31,42,63,0.04)",
        display: "flex", flexDirection: "column", gap: 8, textAlign: "left",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
        <span style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0, flex: 1 }}>
          <IconBubble icon={icon} accent={accent} />
          <span style={{ fontSize: LABEL_FONT_SIZE, fontWeight: 700, color: T.text.primary, textTransform: "uppercase", letterSpacing: "0.06em", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{label}</span>
        </span>
        {/* Arrow icon ONLY when the tile actually opens something. Avoids
            the "looks clickable but does nothing" UX trap. */}
        {interactive && <ExpandButton />}
      </div>

      <div style={{ fontSize: VALUE_FONT_SIZE, fontWeight: 700, lineHeight: 1.0, color: T.text.primary, fontVariantNumeric: "tabular-nums", fontFeatureSettings: T.font.nums, letterSpacing: "-0.025em" }}>{value}</div>

      <div style={{ display: "flex", alignItems: "center", gap: 8, minHeight: 16 }}>
        {delta && <DeltaText direction={delta.direction} value={delta.value} unit={delta.unit} />}
        {subValue && (
          <span style={{ fontSize: META_FONT_SIZE, color: T.text.muted, fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {delta ? "·  " : ""}{subValue}
          </span>
        )}
      </div>

      {breakdown && breakdown.length > 0 && (
        <BreakdownRow items={breakdown} color={accent || T.brand.base} />
      )}

      <div style={{ flex: 1 }} />

      {sparkline && <Sparkline data={sparkline} color={accent || T.brand.base} />}
    </button>
  );
}
